import { createEffect, createMemo, createRoot } from "solid-js";
import { createStore } from "solid-js/store";

import { DrawerItemType } from "@/hooks/wallet/useWallet.tsx";
import { formatToBindPoint, isFiat } from "@/lib/pointUtils";
import { CryptoConfItem } from "@/types/crypto.ts";
import { PayChannel } from "@/types/swagger/front.ts";
import { WalletType } from "@/types/wallet.ts";

import { cryptoStore } from "./cryptoStore.ts";
import { getUserPointInfo, userStore } from "./userStore.ts";

export type CurrentPayInfoType = PayChannel & {
  pay_name?: string;
  count?: number;
  lang_text?: LangTextSingle;
};

export type LangTextSingle = {
  name: string;
  tip: string;
  desc?: string;
  video_url?: string;
  video_desc?: string;
};

export type PointStatusType = "none" | "rollover" | "redeemable";

interface DefaultClass {
  class: string;
  list: CurrentPayInfoType[];
  lang_text?: LangTextSingle;
}
export type FiatClassMap = Record<number | string, DefaultClass>;

interface TWalletStore {
  usdt_rate: number; //USDT兑换法币的汇率, *100
  pay_list: CurrentPayInfoType[];
  currentPointInfo: CryptoConfItem; //选中的货币类型
  crypto_list: CurrentPayInfoType[];
  pait_list: FiatClassMap;
  pointType: number;
  walletList: WalletType[];
  selectedWallet: WalletType | null;
  walletItemList: DrawerItemType[];
  cryptoWalletList: WalletType[];
  fiatWalletList: WalletType[];
  pointStatus: PointStatusType;
}

export const [walletStore, setWalletStore] = createStore<TWalletStore>({
  usdt_rate: 0,
  pay_list: [],
  currentPointInfo: {} as CryptoConfItem,
  crypto_list: [],
  pait_list: {},
  pointType: 5,
  walletList: [],
  selectedWallet: null,
  walletItemList: [],
  cryptoWalletList: [],
  fiatWalletList: [],
  pointStatus: "none",
});

export const getcurrentPointInfo = (pointType: number) => {
  return cryptoStore.fiat_currency_config.find(
    (item) => item.point_type == pointType,
  );
};
// 当前绑定货币本金+彩金
export const bindTotalPoint = createRoot(() =>
  createMemo(() => {
    return formatToBindPoint({
      value:
        (walletStore.selectedWallet?.point || 0) +
        (walletStore.selectedWallet?.bonus || 0),
      pointType: walletStore.selectedWallet?.pointType || 0,
      showSymbol: true,
      returnVariable: true,
    }) as { prefix: string; result: string; suffix: string };
  }),
);
// 当前绑定货币本金
export const bindPoint = createRoot(() =>
  createMemo(() => {
    return formatToBindPoint({
      value: walletStore.selectedWallet?.point,
      pointType: walletStore.selectedWallet?.pointType || 0,
      showSymbol: true,
      returnVariable: true,
    }) as { prefix: string; result: string; suffix: string };
  }),
);
// 当前绑定货币彩金
export const bindBonus = createRoot(() =>
  createMemo(() => {
    return formatToBindPoint({
      value: walletStore.selectedWallet?.bonus,
      pointType: walletStore.selectedWallet?.pointType || 0,
      showSymbol: true,
      returnVariable: true,
    }) as { prefix: string; result: string; suffix: string };
  }),
);
// 当前绑定货币锁定彩金
export const bindBonusCode = createRoot(() =>
  createMemo(() => {
    return formatToBindPoint({
      value: walletStore.selectedWallet?.bonusCode,
      pointType: walletStore.selectedWallet?.pointType || 0,
      showSymbol: true,
      isFiatPoint: isFiat(walletStore.selectedWallet?.pointType || 0),
    });
  }),
);

export const bindTotalPointVariable = createRoot(() =>
  createMemo(() => {
    return formatToBindPoint({
      value: walletStore.selectedWallet?.point || 0,
      pointType: walletStore.selectedWallet?.pointType || 0,
      showSymbol: true,
      returnVariable: true,
    }) as { prefix: string; result: string; suffix: string };
  }),
);

createRoot(() =>
  createEffect(() => {
    if (userStore.isLogin) {
      const cryptoList = cryptoStore.crypto_currency_config;
      const fiatList = cryptoStore.fiat_currency_config;
      const cryptoWalletList: WalletType[] = [];
      const fiatWalletList: WalletType[] = [];
      let currentWallet: WalletType | null = null;

      for (const crypto of cryptoList) {
        const pointInfo = getUserPointInfo(crypto.point_type);
        const code = pointInfo?.code;
        const point = pointInfo?.point;
        const bonus = pointInfo?.bonus;
        const bonusCode = pointInfo?.bonusCode;
        const wallet: WalletType = {
          pointType: crypto.point_type,
          pointName: crypto.point_name as string,
          point: point || 0,
          code: code || 0,
          bonus: bonus || 0,
          bonusCode: bonusCode || 0,
          icon: crypto.icon as string,
          symbol: crypto.symbol as string,
        };

        if (userStore.pointType === crypto.point_type) {
          setWalletStore("selectedWallet", wallet);
          currentWallet = wallet;
        } else {
          cryptoWalletList.push(wallet);
        }
      }
      for (const fiat of fiatList) {
        const pointInfo = getUserPointInfo(fiat.point_type);
        const code = pointInfo?.code;
        const point = pointInfo?.point;
        const bonus = pointInfo?.bonus;
        const bonusCode = pointInfo?.bonusCode;
        const wallet: WalletType = {
          pointType: fiat.point_type,
          pointName: fiat.name as string,
          point: point || 0,
          code: code || 0,
          bonus: bonus || 0,
          bonusCode: bonusCode || 0,
          icon: fiat.icon as string,
          symbol: fiat.symbol as string,
        };
        if (userStore.pointType === fiat.point_type) {
          setWalletStore("selectedWallet", wallet);
          currentWallet = wallet;
        } else {
          fiatWalletList.push(wallet);
        }
      }

      setWalletStore("cryptoWalletList", cryptoWalletList);

      setWalletStore("fiatWalletList", fiatWalletList);

      setWalletStore(
        "walletList",
        [currentWallet, ...cryptoWalletList, ...fiatWalletList]?.filter(
          Boolean,
        ) as WalletType[],
      );

      setWalletStore(
        "walletItemList",
        walletStore.walletList.map((item) => {
          const isSelected =
            walletStore.selectedWallet?.pointType === item.pointType;
          return {
            icon: item.icon,
            pointName: item.pointName,
            point: formatToBindPoint({
              value: item.point,
              pointType: item.pointType,
              showSymbol: isFiat(item.pointType),
              isFiatPoint: isFiat(item.pointType),
            }),
            bonus: formatToBindPoint({
              value: item.bonus,
              pointType: item.pointType,
              showSymbol: isFiat(item.pointType),
              isFiatPoint: isFiat(item.pointType),
            }),
            pointType: item.pointType,
            isSelected,
          };
        }),
      );

      // 计算 pointStatus
      const selectedWallet = walletStore.selectedWallet;
      if (selectedWallet) {
        const bonus = selectedWallet.bonus || 0;
        const bonusCode = selectedWallet.bonusCode || 0;

        let pointStatus: PointStatusType = "none";
        if (bonus > 0 && bonusCode > 0) {
          pointStatus = "rollover";
        } else if (bonus > 0 && bonusCode === 0) {
          pointStatus = "redeemable";
        } else {
          pointStatus = "none";
        }

        setWalletStore("pointStatus", pointStatus);
      }
    }
  }),
);
